import { Icon } from "@focus/focusUI";
import { useMemo, useState } from "react";

import type { InputOrbitRow } from "../../../services/inputs";
import type { InputLog } from "../../../types/inputs";
import { FloatingWindow } from "../../window/FloatingWindow";
import {
  type LogSortBy,
  type LogSeverityFilter,
  type SortDirection,
  InputOrbitLogsToolbar,
} from "./InputLogsToolbar";
import { InputOrbitLogCard } from "./InputOrbitLogCard";

type Props = {
  input: InputOrbitRow | null;
  onClose: () => void;
};

const getLogSortValue = (log: InputLog, sortBy: LogSortBy) => {
  if (sortBy === "timestamp") {
    return new Date(log.timestamp).getTime();
  }

  return log[sortBy].toLowerCase();
};

const filterLogs = (
  logs: InputLog[],
  searchTerm: string,
  severity: LogSeverityFilter,
) => {
  const normalizedSearch = searchTerm.trim().toLowerCase();

  return logs.filter((log) => {
    const matchesSeverity = severity === "all" || log.severity === severity;

    if (!matchesSeverity) {
      return false;
    }

    if (!normalizedSearch) {
      return true;
    }

    return [log.timestamp, log.severity, log.author, log.message].some(
      (value) => value.toLowerCase().includes(normalizedSearch),
    );
  });
};

const sortLogs = (
  logs: InputLog[],
  sortBy: LogSortBy,
  sortDirection: SortDirection,
) =>
  [...logs].sort((a, b) => {
    const aValue = getLogSortValue(a, sortBy);
    const bValue = getLogSortValue(b, sortBy);
    const direction = sortDirection === "asc" ? 1 : -1;

    if (aValue < bValue) return -1 * direction;
    if (aValue > bValue) return 1 * direction;
    return 0;
  });



const InputOrbitLogsList = ({ logs }: { logs: InputLog[] }) => {
  return (
    <div className="grid gap-3">
      {logs.map((log) => (
        <InputOrbitLogCard key={log.id} log={log} />
      ))}
    </div>
  );
};

const InputOrbitLogsEmptyState = ({ hasData }: { hasData: boolean }) => {
  return (
    <div className="flex h-full items-center justify-center">
      <div className="rounded-lg border border-dashed bg-white p-8 text-center">
        <Icon icon="document-open" />

        <h3 className="mt-3 text-sm font-bold">
          {hasData ? "No logs found" : "No logs available"}
        </h3>

        <p className="mt-1 text-sm text-slate-500">
          {hasData
            ? "Try adjusting your filters."
            : "Logs for this input file will appear here."}
        </p>
      </div>
    </div>
  );
};

export const InputOrbitLogsWindow = ({ input, onClose }: Props) => {
  const logs = input?.logs ?? [];
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<LogSortBy>("timestamp");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [severity, setSeverity] = useState<LogSeverityFilter>("all");

  const filteredLogs = useMemo(() => {
    return sortLogs(
      filterLogs(logs, searchTerm, severity),
      sortBy,
      sortDirection,
    );
  }, [logs, searchTerm, severity, sortBy, sortDirection]);

  return (
    <FloatingWindow
      open={input !== null}
      onClose={onClose}
      title={input ? `Logs for ${input.filename}` : "Input logs"}
      icon="document-open"
      defaultWidth={860}
      defaultHeight={420}
    >
      <div className="flex h-full min-h-0 flex-col bg-slate-50/50">
        <InputOrbitLogsToolbar
          filtered={filteredLogs.length}
          total={logs.length}
          searchTerm={searchTerm}
          sortBy={sortBy}
          sortDirection={sortDirection}
          severity={severity}
          onSearchChange={setSearchTerm}
          onSortChange={setSortBy}
          onDirectionChange={setSortDirection}
          onSeverityChange={setSeverity}
        />

        {filteredLogs.length ? (
          <div className="min-h-0 flex-1 overflow-y-auto p-4">
            <InputOrbitLogsList logs={filteredLogs} />
          </div>
        ) : (
          <InputOrbitLogsEmptyState hasData={logs.length > 0} />
        )}
      </div>
    </FloatingWindow>
  );
};
